import { Injectable } from '@angular/core';
import {
  MembersService,
  OrganisationsService,
  SessionsService,
  TenantsService,
} from '@careapp-group/ng-sdk-v2-new';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  forkJoin as observableForkJoin,
  from,
  from as observableFrom,
  Observable,
  of as observableOf,
  of,
} from 'rxjs';
import { switchMap, withLatestFrom, map, catchError, tap } from 'rxjs/operators';
import * as actions from '../actions';
import { FORCE_LOGOUT_401, SessionReadyAction } from '../actions';
import * as selectors from '../reducers';
import { auth } from '../auth/auth.selectors';
import { FrontendApi, Session } from '@ory/client';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import { KratosAuth } from '../auth/auth.reducer';
import { Ability } from '@casl/ability';

export const NO_ORGANISATIONS = 'NO_ORGANISATIONS';
export const NO_USER = 'NO_USER';
export const NO_ROLES = 'NO_ROLES';
export const FAIL_TOKEN_REFRESH = 'FAIL_TOKEN_REFRESH';
const DAY_MS = 1000 * 60 * 60 * 24;

@Injectable()
export class KratosCoreEffects {
  applicationStartup = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RESPONSE_REHYDRATED),
      switchMap((action) =>
        from(this.kratos.toSession()).pipe(
          map((session) => {
            if (session?.data?.id && session?.data?.active) {
              return SessionReadyAction({
                session: session.data,
                userId: session.data.identity.metadata_public['ca_member_id'],
              });
            } else {
              return new actions.ApplicationReady();
            }
          }),
          catchError((err) => of(new actions.ApplicationReady()))
        )
      ),
      catchError((err) => of(new actions.ApplicationReady()))
    )
  );

  // Gets the current session from Kratos and runs through
  // an auth flow like Login just happened. Keeps the user
  // logged in after password reset.
  authRefresh = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RefreshAppStartup),
      switchMap((action) =>
        from(this.kratos.toSession()).pipe(
          map((session) => {
            if (session?.data?.id && session?.data?.active) {
              return actions.ResponseSuccessAuthLoginAction({
                session: session.data,
                userId: session.data.identity.metadata_public['ca_member_id'],
              });
            } else {
              return new actions.ApplicationReady();
            }
          }),
          catchError((err) => of(new actions.ApplicationReady()))
        )
      ),
      catchError((err) => of(new actions.ApplicationReady()))
    )
  );

  fetchUserAfterRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SuccessCompleteRegistrationFlow),
      switchMap((action) =>
        this.membersService
          .getMember({ id: action.response.identity.metadata_public['ca_member_id'] })
          .pipe(
            map(
              (response) => new actions.ResponseSuccessLoggedInUser(response),
              catchError((err) => observableOf(new actions.ResponseFailLoggedInUser(err)))
            )
          )
      )
    )
  );

  refreshAfterAcceptingInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.SuccessAcceptInvite),
      withLatestFrom(this.store.select(auth.getAuth)),
      switchMap(
        ([action, auth]): Observable<any> =>
          this.performStartupFetches(auth.userId).pipe(
            switchMap((results) =>
              observableFrom([
                ...results,
                actions.NavigateAction({
                  path: ['/'],
                }),
              ])
            )
          )
      )
    )
  );

  setAbility$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.ResponseSuccessFetchAbility),
        tap((rules) => {
          this.ability.update(rules.ability);
        })
      ),
    {
      dispatch: false,
    }
  );

  @Effect()
  haveSession = this.actions$.pipe(
    ofType<actions.CAAction>(actions.SessionReadyAction, actions.ResponseSuccessAuthLoginAction),
    withLatestFrom(
      this.store.select(auth.getAuth),
      this.store.select(selectors.getSelectedId('organisations')),
      this.store.select(auth.getRoles),
      this.store.select(auth.getLoggedInUser)
    ),
    switchMap(([action, auth, selectedOrgId, roles, user]): Observable<any> => {
      return this.refreshSessionIfRequired((<KratosAuth>auth).session).pipe(
        switchMap((updatedTokenAction) =>
          this.performStartupFetches(auth.userId).pipe(
            switchMap((results) =>
              this.createObservableStreamFromResults(
                results,
                updatedTokenAction,
                selectedOrgId,
                action
              )
            )
          )
        )
      );
    })
  );

  constructor(
    private actions$: Actions,
    private membersService: MembersService,
    private orgsService: OrganisationsService,
    private sessionsService: SessionsService,
    private tenantsService: TenantsService,
    private store: Store<any>,
    private kratos: FrontendApi,
    private ability: Ability
  ) {}

  createObservableStreamFromResults(
    results: any[],
    updatedTokenAction: any,
    selectedOrgId: string,
    sourceAction: any
  ) {
    const failure = results.find((res) => res.type === FORCE_LOGOUT_401);
    let actionStream: any[] = results.concat([updatedTokenAction, new actions.ApplicationReady()]);
    const [memberResults, roleResults, orgResults, ability] = results;
    if (failure) {
      return observableFrom(actionStream);
    }
    // If the user has any global role, then they are allowed to log in
    if (!roleResults.payload.length) {
      // Filter orgs based on user permissions (exclude non dashboard roles)
      const authorizedOrgs = orgResults.payload;
    }
    const orgId = selectedOrgId || orgResults.payload[0]?.id;

    actionStream = actionStream.concat(
      new actions.ApplicationAuthorized({
        user: memberResults.payload,
        orgId,
        roles: roleResults.payload,
      })
    );
    if (sourceAction.type === actions.RESPONSE_SUCCESS_AUTH_LOGIN) {
      actionStream = actionStream.concat(new actions.ApplicationLoginComplete());
    }
    return observableFrom(actionStream);
  }

  performStartupFetches(userId: string) {
    return observableForkJoin([
      this.membersService.getMember({ id: userId }).pipe(
        map((updatedUser: any) => new actions.ResponseSuccessLoggedInUser(updatedUser)),
        catchError((err) => observableOf(new actions.ForceLogout401(NO_USER)))
      ),
      this.membersService.getMemberRoles({ id: userId }).pipe(
        map((updatedRoles: string[]) => new actions.ResponseSuccessUserRoles(updatedRoles)),
        catchError((err) => observableOf(new actions.ForceLogout401(NO_ROLES)))
      ),
      this.tenantsService.getTenants({}).pipe(
        map((tenants) => actions.Tenants.ResponseSuccessAllAction(tenants)),
        catchError((err) => {
          return observableOf(actions.Organisations.ResponseFailAllAction(err));
        })
      ),
      this.orgsService.listOrganisations().pipe(
        map((organisations) => actions.Organisations.ResponseSuccessOwn(organisations)),
        catchError((err) => {
          return observableOf(actions.Organisations.ResponseFailAllAction(err));
        })
      ),
      this.membersService.getAbility({ id: userId }).pipe(
        map((response) => actions.ResponseSuccessFetchAbility({ ability: response })),
        catchError((err) => observableOf(new actions.ForceLogout401(NO_ROLES)))
      ),
    ]);
  }

  refreshSessionIfRequired(session: Session) {
    // TODO Replace this with new refresh endpoint
    let msSinceCreated;
    if (session) {
      msSinceCreated = differenceInMilliseconds(new Date(), parseISO(session.authenticated_at));
    } else {
      return observableOf(new actions.ForceLogout401(FAIL_TOKEN_REFRESH));
    }
    if (msSinceCreated > DAY_MS) {
      return this.sessionsService.refreshSession().pipe(
        map(() => {
          return actions.ResponseSuccessSessionRefreshAction({});
        }),
        catchError((ex) => {
          if (ex.statusCode === 401) {
            return observableFrom([
              new actions.ResponseFailAuthRefreshAction(ex),
              new actions.ForceLogout401(FAIL_TOKEN_REFRESH),
            ]);
          } else {
            // Failed to refresh for other reason - use stale token.
            return observableOf(new actions.ResponseFailAuthRefreshAction(auth));
          }
        })
      );
    } else {
      return observableOf(actions.ResponseSuccessSessionRefreshAction({}));
    }
  }
}
